import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  deleteUser, generateUserMagicLink, getUserClientAccess, inviteUser,
  listClients, listUsers, setUserClientAccess,
  updateUser, type MagicLinkResponse, type UserDto,
} from '../lib/api';
import type { Client, PageId, Role } from '../types';
import { GBIBadge, GBIBtn, GBICard, T, sharedStyles } from '../components/Shared';

// All pages that exist in the app (must mirror functions/lib/auth.ts ALL_PAGES).
// 'citations' is omitted from the matrix even though it exists in the type
// union — it's a back-compat entry not surfaced in the sidebar anymore.
const ALL_PAGES: PageId[] = ['dashboard', 'scan', 'analysis', 'heatmap', 'competitors', 'settings', 'users', 'intake'];

// Friendly labels for the column headers. Keep short — column width is tight.
const PAGE_LABEL: Record<PageId, string> = {
  dashboard:   'Dashboard',
  scan:        'AI Visibility',
  analysis:    'AI Insight',
  heatmap:     'Heatmap',
  competitors: 'Competitors',
  citations:   'Citations',
  settings:    'Settings',
  users:       'Users',
  intake:      'Intake',
};

const ROLES: Role[] = ['admin', 'manager', 'staff', 'client'];

// MUST mirror functions/lib/auth.ts DEFAULT_PAGES. Staff + client default to
// empty so admins explicitly grant access via the matrix below.
const ROLE_DEFAULTS: Record<Role, PageId[]> = {
  admin:   ['dashboard', 'scan', 'analysis', 'heatmap', 'competitors', 'settings', 'users', 'intake'],
  manager: ['dashboard', 'scan', 'analysis', 'heatmap', 'competitors', 'intake'],
  staff:   [],
  client:  ['dashboard', 'scan', 'analysis', 'heatmap', 'competitors'],
};

const ROLE_COLORS: Record<Role, string> = {
  admin:   T.purple,
  manager: T.cyan,
  staff:   T.fgSecondary,
  client:  T.amber,
};

// Admin role always sees every page — checkboxes are locked.
const ADMIN_OVERRIDE = true;

export function AdminUsers() {
  const [users, setUsers] = useState<UserDto[] | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [editing, setEditing] = useState<UserDto | null>(null);
  const [magicLink, setMagicLink] = useState<MagicLinkResponse | null>(null);
  const [generatingFor, setGeneratingFor] = useState<string | null>(null);
  // User whose per-client access drawer is open.
  const [clientAccessFor, setClientAccessFor] = useState<UserDto | null>(null);
  // Per-cell saving indicator: `${userId}|${page}` → boolean
  const [savingCells, setSavingCells] = useState<Set<string>>(new Set());
  // Filter to show only one role's users in the matrix.
  const [roleFilter, setRoleFilter] = useState<Role | 'all'>('all');
  // Free-text search across name / email / linked client id.
  const [search, setSearch] = useState('');

  const refresh = async () => {
    try {
      const [u, c] = await Promise.all([listUsers(), listClients()]);
      setUsers(u);
      setClients(c);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => { void refresh(); }, []);

  // Resolve a user's effective allowed pages — custom list if set, else role default.
  const effectivePages = (u: UserDto): PageId[] => {
    if (u.role === 'admin' && ADMIN_OVERRIDE) return ALL_PAGES;
    return u.allowedPages ?? ROLE_DEFAULTS[u.role];
  };

  // Toggle one page for one user. Saves immediately to the backend.
  const togglePage = async (u: UserDto, page: PageId) => {
    if (u.role === 'admin' && ADMIN_OVERRIDE) return; // locked
    const cellKey = `${u.id}|${page}`;
    if (savingCells.has(cellKey)) return;

    const current = effectivePages(u);
    const next = current.includes(page)
      ? current.filter(p => p !== page)
      : [...current, page];

    setSavingCells(prev => new Set(prev).add(cellKey));
    // Optimistic update — the row reflects the new value immediately while
    // the PATCH is in flight. We refresh from the server at the end to
    // converge on truth (handles concurrent admin edits in another tab).
    setUsers(prev => prev?.map(x => x.id === u.id ? { ...x, allowedPages: next } : x) ?? prev);
    try {
      await updateUser(u.id, { allowedPages: next });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // Roll back optimistic update.
      await refresh();
    } finally {
      setSavingCells(prev => {
        const n = new Set(prev);
        n.delete(cellKey);
        return n;
      });
    }
  };

  // Reset a user to their role's default (set allowedPages = null on the server).
  const resetToDefault = async (u: UserDto) => {
    if (u.role === 'admin' && ADMIN_OVERRIDE) return;
    setUsers(prev => prev?.map(x => x.id === u.id ? { ...x, allowedPages: null as unknown as PageId[] } : x) ?? prev);
    try {
      await updateUser(u.id, { allowedPages: null });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      await refresh();
    }
  };

  // Set a user to "no access at all" (empty array — not the same as null/default).
  const revokeAll = async (u: UserDto) => {
    if (u.role === 'admin' && ADMIN_OVERRIDE) return;
    if (!confirm(`Revoke all page access for ${u.email}? They'll see an empty dashboard until you grant pages back.`)) return;
    setUsers(prev => prev?.map(x => x.id === u.id ? { ...x, allowedPages: [] } : x) ?? prev);
    try {
      await updateUser(u.id, { allowedPages: [] });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      await refresh();
    }
  };

  const handleGenerateLink = async (userId: string) => {
    setGeneratingFor(userId);
    try {
      const res = await generateUserMagicLink(userId);
      setMagicLink(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setGeneratingFor(null);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!users) return null;
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (!q) return true;
      return (
        (u.name ?? '').toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.clientId ?? '').toLowerCase().includes(q)
      );
    });
  }, [users, roleFilter, search]);

  return (
    // Own scroll container: global CSS locks body{overflow:hidden} on desktop,
    // so the page must scroll itself or long user lists get clipped.
    <div style={{ height: '100vh', overflowY: 'auto', background: '#f8f9fa', padding: '40px 32px' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 }}>
          <div>
            <Link to="/admin" style={{ fontSize: 12, color: T.fgSecondary, textDecoration: 'none' }}>← Back to clients</Link>
            <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 28, fontWeight: 700, color: T.fgPrimary, marginTop: 8 }}>
              Users & Permissions
            </div>
            <div style={{ fontSize: 13, color: T.fgSecondary, marginTop: 4 }}>
              {users ? `${users.length} accounts` : 'Loading…'} · click a cell to toggle page access
            </div>
          </div>
          <GBIBtn variant="primary" onClick={() => setShowInvite(true)}>+ Invite User</GBIBtn>
        </div>

        {error && (
          <GBICard style={{ borderColor: T.magenta, color: T.magenta, marginBottom: 16, cursor: 'pointer' }}
            onClick={() => setError(null)}>
            <strong>Action failed.</strong>
            <div style={{ fontSize: 12, marginTop: 6, fontFamily: sharedStyles.fontMono }}>{error}</div>
            <div style={{ fontSize: 10, color: T.fgMuted, marginTop: 8 }}>Click to dismiss.</div>
          </GBICard>
        )}

        {/* Search + role filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '0 1 320px', minWidth: 220 }}>
            <span style={{
              position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              fontSize: 12, color: T.fgMuted, pointerEvents: 'none',
            }}>🔍</span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, email, or client…"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '8px 10px 8px 30px', fontSize: 12,
                border: `1px solid ${T.border}`, borderRadius: 8,
                outline: 'none', color: T.fgPrimary, background: '#fff',
              }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                title="Clear search"
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  fontSize: 12, color: T.fgMuted,
                }}
              >✕</button>
            )}
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: T.fgMuted, textTransform: 'uppercase' }}>Role</span>
          <FilterPill active={roleFilter === 'all'} onClick={() => setRoleFilter('all')} label="All" />
          {ROLES.map(r => (
            <FilterPill
              key={r}
              active={roleFilter === r}
              onClick={() => setRoleFilter(r)}
              label={r}
              color={ROLE_COLORS[r]}
            />
          ))}
          {filteredUsers && (
            <span style={{ fontSize: 11, color: T.fgMuted, marginLeft: 'auto' }}>
              {filteredUsers.length} shown
            </span>
          )}
        </div>

        {/* Permission matrix */}
        <GBICard style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
            <thead>
              <tr style={{ background: T.bgSubtle }}>
                <th style={thStyle()}>User</th>
                <th style={thStyle()}>Role</th>
                <th style={thStyle()}>Client</th>
                {ALL_PAGES.map(p => (
                  <th key={p} style={{ ...thStyle(), textAlign: 'center', width: 80 }}>
                    {PAGE_LABEL[p]}
                  </th>
                ))}
                <th style={{ ...thStyle(), textAlign: 'right', width: 300, minWidth: 300 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers?.map(u => {
                const eff = effectivePages(u);
                const isDefault = u.allowedPages === null || u.allowedPages === undefined;
                const isAdmin = u.role === 'admin' && ADMIN_OVERRIDE;
                return (
                  <tr key={u.id} style={{
                    borderTop: `1px solid ${T.border}`,
                    opacity: u.status === 'disabled' ? 0.45 : 1,
                  }}>
                    <td style={tdStyle()}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.fgPrimary }}>{u.name ?? u.email}</div>
                      <div style={{ fontSize: 10, color: T.fgMuted, fontFamily: sharedStyles.fontMono, marginTop: 2 }}>{u.email}</div>
                    </td>
                    <td style={tdStyle()}>
                      <GBIBadge color={ROLE_COLORS[u.role]}>{u.role}</GBIBadge>
                      {u.status === 'disabled' && (
                        <div style={{ marginTop: 4, fontSize: 9, color: T.magenta, fontWeight: 700, letterSpacing: '0.08em' }}>DISABLED</div>
                      )}
                    </td>
                    <td style={tdStyle()}>
                      <span style={{ fontSize: 11, color: T.fgSecondary, fontFamily: sharedStyles.fontMono }}>
                        {u.clientId ?? '—'}
                      </span>
                    </td>
                    {ALL_PAGES.map(p => {
                      const allowed = eff.includes(p);
                      const saving = savingCells.has(`${u.id}|${p}`);
                      return (
                        <td key={p} style={{ ...tdStyle(), textAlign: 'center', padding: 8 }}>
                          <button
                            onClick={() => togglePage(u, p)}
                            disabled={isAdmin || saving || u.status === 'disabled'}
                            title={isAdmin ? 'Admins see every page (locked)' : `Toggle ${PAGE_LABEL[p]} for ${u.email}`}
                            style={{
                              width: 26, height: 26, borderRadius: 6,
                              border: `1px solid ${allowed ? (isAdmin ? T.purple : T.cyan) : T.border}`,
                              background: allowed ? (isAdmin ? T.purple : T.cyan) : 'transparent',
                              color: allowed ? '#fff' : 'transparent',
                              fontSize: 14, fontWeight: 700,
                              cursor: isAdmin || u.status === 'disabled' ? 'not-allowed' : (saving ? 'wait' : 'pointer'),
                              opacity: saving ? 0.6 : 1,
                              transition: 'all 120ms',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              padding: 0,
                            }}
                          >
                            {allowed ? '✓' : ''}
                          </button>
                        </td>
                      );
                    })}
                    <td style={{ ...tdStyle(), textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'nowrap' }}>
                        {!isAdmin && (
                          <button
                            onClick={() => isDefault ? revokeAll(u) : resetToDefault(u)}
                            title={isDefault
                              ? `Currently using ${u.role} default (${ROLE_DEFAULTS[u.role].length} pages). Click to revoke all.`
                              : 'Custom permissions set. Click to reset to role default.'}
                            style={{
                              background: 'transparent',
                              border: `1px solid ${isDefault ? T.border : T.cyan}`,
                              color: isDefault ? T.fgMuted : T.cyan,
                              borderRadius: 4, padding: '3px 8px',
                              fontSize: 10, fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            {isDefault ? 'default' : 'custom'}
                          </button>
                        )}
                        <button
                          onClick={() => handleGenerateLink(u.id)}
                          disabled={u.status === 'disabled' || generatingFor === u.id}
                          title="Generate sign-in link"
                          style={{
                            background: 'transparent', border: `1px solid ${T.cyan}`,
                            borderRadius: 4, padding: '3px 8px', fontSize: 10,
                            cursor: u.status === 'disabled' ? 'not-allowed' : 'pointer',
                            color: T.cyan, fontWeight: 600,
                            opacity: u.status === 'disabled' ? 0.4 : 1,
                          }}
                        >🔗 {generatingFor === u.id ? '…' : 'Link'}</button>
                        {(u.role === 'staff' || u.role === 'manager') && (
                          <button
                            onClick={() => setClientAccessFor(u)}
                            title="Choose which clients this user can access"
                            style={{
                              background: 'transparent', border: `1px solid ${T.amber}`,
                              borderRadius: 4, padding: '3px 8px', fontSize: 10,
                              cursor: 'pointer', color: T.amber, fontWeight: 600,
                            }}
                          >🏢 Clients</button>
                        )}
                        <button onClick={() => setEditing(u)} style={{
                          background: 'transparent', border: `1px solid ${T.border}`,
                          borderRadius: 4, padding: '3px 8px', fontSize: 10,
                          cursor: 'pointer', color: T.fgSecondary,
                        }}>Edit</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredUsers?.length === 0 && (
                <tr>
                  <td colSpan={3 + ALL_PAGES.length + 1} style={{
                    padding: 30, textAlign: 'center', color: T.fgMuted,
                    fontSize: 13, fontStyle: 'italic',
                  }}>
                    No users match this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </GBICard>

        {/* Help text */}
        <div style={{
          marginTop: 16, padding: '12px 16px',
          background: 'rgba(18,181,196,0.06)', border: `1px solid ${T.cyan}`,
          borderRadius: 8, fontSize: 12, color: T.fgSecondary, lineHeight: 1.6,
        }}>
          <strong style={{ color: T.cyan }}>How permissions work:</strong> Each checkbox grants or revokes one page for one user. Changes save instantly.
          <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
            <li><strong>Admin</strong> always sees every page (locked).</li>
            <li><strong>Manager</strong> defaults to the full client dashboard except the Users page.</li>
            <li><strong>Staff</strong> &amp; <strong>Client</strong> default to <em>nothing</em> — you must explicitly tick the pages they should see.</li>
            <li>The <em>default ↔ custom</em> button toggles between "use role default" (null) and "use exact ticked pages" (custom). Revoke-all is the same button after defaults are dropped.</li>
          </ul>
        </div>
      </div>

      {showInvite && (
        <UserFormModal
          mode="invite"
          clients={clients}
          onClose={() => setShowInvite(false)}
          onSaved={() => { setShowInvite(false); void refresh(); }}
        />
      )}
      {editing && (
        <UserFormModal
          mode="edit"
          user={editing}
          clients={clients}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void refresh(); }}
        />
      )}
      {magicLink && (
        <MagicLinkModal data={magicLink} onClose={() => setMagicLink(null)} />
      )}
      {clientAccessFor && (
        <ClientAccessModal
          user={clientAccessFor}
          allClients={clients}
          onClose={() => setClientAccessFor(null)}
          onSaved={() => { setClientAccessFor(null); void refresh(); }}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Per-user client access — searchable, tickable client list.
// Empty selection = "no explicit grants" → staff/manager fall back to
// seeing ALL clients (back-compat, see migration 0014). Selecting ≥1 client
// flips the user into whitelist mode (only those clients).

function ClientAccessModal({ user, allClients, onClose, onSaved }: {
  user: UserDto;
  allClients: Client[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    let cancelled = false;
    getUserClientAccess(user.id)
      .then(ids => { if (!cancelled) setSelected(new Set(ids)); })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : String(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user.id]);

  const toggle = (id: string) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return allClients;
    return allClients.filter(c =>
      c.name.toLowerCase().includes(s) ||
      c.location.toLowerCase().includes(s) ||
      (c.groupLabel ?? '').toLowerCase().includes(s),
    );
  }, [allClients, q]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await setUserClientAccess(user.id, [...selected]);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  };

  const unrestricted = selected.size === 0;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 24,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, padding: 24, width: 560, maxWidth: '100%',
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 18, fontWeight: 700, color: T.fgPrimary }}>
          Client access — {user.name ?? user.email}
        </div>
        <div style={{ fontSize: 12, color: T.fgSecondary, margin: '6px 0 14px', lineHeight: 1.5 }}>
          Tick the clients this {user.role} may open. <strong>Leave everything unticked</strong> to give them
          access to <em>all</em> clients (no restriction). Tick ≥1 to lock them to exactly those.
        </div>

        <div style={{
          padding: '8px 12px', marginBottom: 12, borderRadius: 6,
          fontSize: 12, fontWeight: 600,
          background: unrestricted ? 'rgba(245,160,32,0.10)' : 'rgba(18,181,196,0.10)',
          border: `1px solid ${unrestricted ? T.amber : T.cyan}`,
          color: unrestricted ? T.amber : T.cyan,
        }}>
          {unrestricted
            ? '⚠ No restriction — this user can currently see ALL clients.'
            : `🔒 Whitelisted to ${selected.size} client${selected.size === 1 ? '' : 's'}.`}
        </div>

        <input
          autoFocus
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search clients by name, location, or group…"
          style={{
            width: '100%', boxSizing: 'border-box', padding: '9px 12px',
            fontSize: 13, border: `1px solid ${T.border}`, borderRadius: 8,
            outline: 'none', marginBottom: 10,
          }}
        />

        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, minHeight: 120 }}>
          {loading && <div style={{ padding: 16, fontSize: 12, color: T.fgMuted }}>Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div style={{ padding: 16, fontSize: 12, color: T.fgMuted, fontStyle: 'italic' }}>No clients match.</div>
          )}
          {!loading && filtered.map(c => {
            const on = selected.has(c.id);
            return (
              <button
                key={c.id}
                onClick={() => toggle(c.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
                  padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                  background: on ? 'rgba(18,181,196,0.07)' : '#fff',
                  border: `1px solid ${on ? T.cyan : T.border}`,
                }}
              >
                <span style={{
                  width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                  border: `1px solid ${on ? T.cyan : T.border}`,
                  background: on ? T.cyan : 'transparent',
                  color: '#fff', fontSize: 12, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{on ? '✓' : ''}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.fgPrimary }}>{c.name}</span>
                  <span style={{ display: 'block', fontSize: 10, color: T.fgMuted, marginTop: 1 }}>
                    📍 {c.location}{c.groupLabel ? ` · 🏢 ${c.groupLabel}` : ''}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {error && (
          <div style={{
            marginTop: 12, padding: '8px 12px',
            background: 'rgba(232,80,26,0.08)', border: `1px solid ${T.magenta}`,
            borderRadius: 6, fontSize: 12, color: T.magenta,
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <button
            onClick={() => setSelected(new Set())}
            style={{
              background: 'transparent', border: `1px solid ${T.border}`,
              borderRadius: 6, padding: '7px 14px', cursor: 'pointer',
              fontSize: 12, color: T.fgSecondary,
            }}
          >Clear all (= unrestricted)</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{
              background: 'transparent', border: `1px solid ${T.border}`,
              borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 13, color: T.fgSecondary,
            }}>Cancel</button>
            <GBIBtn variant="primary" onClick={save} disabled={saving || loading}>
              {saving ? 'Saving…' : 'Save access'}
            </GBIBtn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────

function FilterPill({ active, onClick, label, color }: {
  active: boolean; onClick: () => void; label: string; color?: string;
}) {
  const c = color ?? T.cyan;
  return (
    <button onClick={onClick} style={{
      padding: '4px 10px', borderRadius: 14, fontSize: 11, fontWeight: 600,
      textTransform: 'capitalize', cursor: 'pointer',
      background: active ? c : 'transparent',
      color: active ? '#fff' : T.fgSecondary,
      border: `1px solid ${active ? c : T.border}`,
    }}>{label}</button>
  );
}

function thStyle(): React.CSSProperties {
  return {
    padding: '12px 14px',
    fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: T.fgMuted,
    textAlign: 'left',
    borderBottom: `1px solid ${T.border}`,
    background: T.bgSubtle,
    position: 'sticky', top: 0, zIndex: 1,
  };
}

function tdStyle(): React.CSSProperties {
  return {
    padding: '12px 14px',
    verticalAlign: 'middle',
    fontSize: 12, color: T.fgPrimary,
  };
}

// ──────────────────────────────────────────────────────────────
// Magic link modal — unchanged from previous version.

interface MagicLinkModalProps {
  data: MagicLinkResponse;
  onClose: () => void;
}

function MagicLinkModal({ data, onClose }: MagicLinkModalProps) {
  const [copied, setCopied] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(() => Math.max(0, Math.floor((data.expiresAt - Date.now()) / 1000)));

  useEffect(() => {
    const id = setInterval(() => {
      setSecondsLeft(Math.max(0, Math.floor((data.expiresAt - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [data.expiresAt]);

  const copy = async () => {
    await navigator.clipboard.writeText(data.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const expired = secondsLeft <= 0;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, padding: 28, width: 540, maxWidth: '90vw',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 20, fontWeight: 700, marginBottom: 6 }}>
          Sign-in link for {data.email}
        </div>
        <div style={{ fontSize: 13, color: T.fgSecondary, marginBottom: 16 }}>
          Send this link to the user via WhatsApp, Slack, or any other channel.
          They click the link and they're signed in — for 30 days.
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px', marginBottom: 14,
          background: expired ? 'rgba(232,80,26,0.08)' : 'rgba(245,160,32,0.08)',
          border: `1px solid ${expired ? T.magenta : T.amber}`,
          borderRadius: 6, fontSize: 12,
          color: expired ? T.magenta : T.amber, fontWeight: 600,
        }}>
          {expired
            ? '⚠️ Expired — close this and click 🔗 Link again to generate a fresh one.'
            : `⏳ Expires in ${minutes}:${seconds.toString().padStart(2, '0')}`}
        </div>

        <div style={{
          display: 'flex', gap: 8, padding: '12px 14px',
          background: '#f8f9fa', border: `1px solid ${T.border}`, borderRadius: 8,
          marginBottom: 16,
        }}>
          <input readOnly value={data.link} style={{
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            fontFamily: sharedStyles.fontMono, fontSize: 11, color: T.fgPrimary,
          }} />
          <button onClick={copy} disabled={expired} style={{
            background: expired ? T.fgMuted : 'linear-gradient(90deg,#12B5C4,#1E7FD4)',
            color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px',
            fontWeight: 600, fontSize: 12, cursor: expired ? 'not-allowed' : 'pointer',
          }}>{copied ? 'Copied!' : 'Copy link'}</button>
        </div>

        <div style={{ fontSize: 11, color: T.fgMuted, lineHeight: 1.5 }}>
          💡 The user only needs this once — after first sign-in, their session lasts 30 days.
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button onClick={onClose} style={{
            background: 'transparent', border: `1px solid ${T.border}`,
            borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: T.fgSecondary,
          }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Invite/Edit modal — kept for changing role / name / client / disable / delete.
// Page checkbox editing lives in the matrix above for the daily-use flow.

interface UserFormModalProps {
  mode: 'invite' | 'edit';
  user?: UserDto;
  clients: Client[];
  onClose: () => void;
  onSaved: () => void;
}

function UserFormModal({ mode, user, clients, onClose, onSaved }: UserFormModalProps) {
  const [email, setEmail] = useState(user?.email ?? '');
  const [name, setName] = useState(user?.name ?? '');
  const [role, setRole] = useState<Role>(user?.role ?? 'staff');
  const [clientId, setClientId] = useState<string>(user?.clientId ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // When role changes, only update the role + client linkage. Allowed pages
  // are intentionally left untouched here so the admin doesn't accidentally
  // wipe a staff member's curated permissions by re-saving the modal. To
  // adjust pages, use the matrix.

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        email: email.trim(),
        name: name.trim() || null,
        role,
        clientId: role === 'client' ? clientId || null : null,
        // Don't touch allowedPages from here — managed in the matrix.
        // For NEW invites, leave it null so the role default applies; admin
        // can grant access in the matrix afterwards.
      };
      if (mode === 'invite') {
        await inviteUser({ ...payload, name: payload.name ?? undefined } as Parameters<typeof inviteUser>[0]);
      } else if (user) {
        const { email: _email, ...patchable } = payload;
        await updateUser(user.id, patchable);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async () => {
    if (!user) return;
    if (!confirm(`Disable ${user.email}? Their active sessions will be killed.`)) return;
    setSubmitting(true);
    try {
      await updateUser(user.id, { status: 'disabled' });
      onSaved();
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnable = async () => {
    if (!user) return;
    setSubmitting(true);
    try {
      await updateUser(user.id, { status: 'active' });
      onSaved();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    if (!confirm(`Permanently delete ${user.email}? This cannot be undone.`)) return;
    setSubmitting(true);
    try {
      await deleteUser(user.id);
      onSaved();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
      padding: 24, overflowY: 'auto',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, padding: 28, width: 520, maxWidth: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 20, fontWeight: 700, marginBottom: 6 }}>
          {mode === 'invite' ? 'Invite user' : `Edit ${user?.email}`}
        </div>
        <div style={{ fontSize: 12, color: T.fgMuted, marginBottom: 18, lineHeight: 1.5 }}>
          {mode === 'invite'
            ? 'After inviting, tick the pages they can see in the matrix. Staff and clients default to no access.'
            : 'Page permissions are managed in the matrix — close this modal to edit them.'}
        </div>

        <Field label="Email">
          <input
            type="email" value={email} onChange={e => setEmail(e.target.value)}
            disabled={mode === 'edit'}
            placeholder="user@example.com"
            style={inputStyle(mode === 'edit')}
          />
        </Field>

        <Field label="Name (optional)">
          <input
            value={name} onChange={e => setName(e.target.value)}
            placeholder="Display name"
            style={inputStyle(false)}
          />
        </Field>

        <Field label="Role">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {ROLES.map(r => (
              <button key={r} onClick={() => {
                setRole(r);
                if (r !== 'client') setClientId('');
              }} style={{
                padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                cursor: 'pointer',
                background: role === r ? ROLE_COLORS[r] : 'transparent',
                color: role === r ? '#fff' : T.fgSecondary,
                border: `1px solid ${role === r ? ROLE_COLORS[r] : T.border}`,
              }}>{r}</button>
            ))}
          </div>
        </Field>

        {role === 'client' && (
          <Field label="Client (which business they can see)">
            <select value={clientId} onChange={e => setClientId(e.target.value)} style={inputStyle(false)}>
              <option value="">Select a client…</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name} — {c.location}</option>
              ))}
            </select>
          </Field>
        )}

        {error && (
          <div style={{
            marginTop: 12, padding: '10px 12px',
            background: 'rgba(232,80,26,0.08)', border: `1px solid ${T.magenta}`,
            borderRadius: 6, fontSize: 12, color: T.magenta,
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {mode === 'edit' && user && user.status === 'active' && (
              <button onClick={handleDisable} disabled={submitting} style={dangerBtn}>Disable</button>
            )}
            {mode === 'edit' && user && user.status === 'disabled' && (
              <button onClick={handleEnable} disabled={submitting} style={ghostBtn}>Enable</button>
            )}
            {mode === 'edit' && user && (
              <button onClick={handleDelete} disabled={submitting} style={dangerBtn}>Delete</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={ghostBtn}>Cancel</button>
            <GBIBtn variant="primary" onClick={submit}>
              {submitting ? 'Saving…' : (mode === 'invite' ? 'Invite' : 'Save')}
            </GBIBtn>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.fgMuted, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

function inputStyle(disabled: boolean): React.CSSProperties {
  return {
    width: '100%', padding: '10px 12px',
    background: disabled ? '#f1f3f4' : '#f8f9fa',
    border: `1px solid ${T.border}`, borderRadius: 6,
    fontFamily: sharedStyles.fontBody, fontSize: 13, color: T.fgPrimary,
    outline: 'none',
  };
}

const ghostBtn: React.CSSProperties = {
  background: 'transparent', border: `1px solid ${T.border}`,
  borderRadius: 6, padding: '6px 14px', cursor: 'pointer',
  fontSize: 13, color: T.fgSecondary,
};
const dangerBtn: React.CSSProperties = {
  background: 'transparent', border: `1px solid ${T.magenta}`, color: T.magenta,
  borderRadius: 6, padding: '6px 14px', cursor: 'pointer',
  fontSize: 13, fontWeight: 600,
};
